import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { Checkbox } from '@components/checkbox';
import { UploadedFile } from '@components/file-field';
import { IconButton } from '@components/icon-button';
import { IconFormIndicatorPending } from '@components/icons/form-indicator-pending';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconRedo } from '@components/icons/redo';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { Spinner } from '@components/loading-spinner';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { useClipboard } from '@hooks/use-clipboard';
import { AdminSiteLanguageType } from '@locale/locale-store';
import { abbreviateDate } from '@utils/date';
import { validateEmail } from '@utils/validators';

import { IntegrationHeader } from 'components/IntegrationHeader';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  FileProps,
  FormResponse,
  onboardingFormPageData,
  RenderFormProps,
  sendOnboardingForm,
  SubmitError,
  Template,
  TriggerlogData,
} from './api/requests';

export const OnboardingFormOnboarding = (props: { applicationId: string }) => {
  const { applicationId } = props;
  const { showError } = useToaster();
  const { t } = useStore();

  const [newForm, setNewForm] = useState(true);

  const formQuery = useQuery({
    queryKey: ['onboarding-form-data', applicationId],
    queryFn: () => onboardingFormPageData(applicationId),
    onSuccess: (data: FormResponse) => {
      if (data.applicationForm) {
        setNewForm(false);
      }
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });

  if (formQuery.isError) {
    return null;
  }
  if (formQuery.isSuccess) {
    const applicationForm = formQuery.data.applicationForm;

    return (
      <Stack space={8}>
        <IntegrationHeader
          integration={{
            name: formQuery.data.name,
            title: formQuery.data.title,
            description: t('integrations.onboardingform.integrationHeader.description'),
          }}
        />
        {newForm ? (
          <NewForm
            data={formQuery.data}
            oneTemplate={formQuery.data.templates.length === 1}
            applicationId={applicationId}
          />
        ) : applicationForm?.completed ? (
          <CompletedForm data={formQuery.data} />
        ) : (
          <InProgressForm
            data={formQuery.data}
            oneTemplate={formQuery.data.templates.length === 1}
            applicationId={applicationId}
          />
        )}
      </Stack>
    );
  }

  return <Spinner />;
};

export const NewForm = (props: {
  data: FormResponse;
  oneTemplate: boolean;
  applicationId: string;
  resend?: boolean;
  closeModal?: () => void;
}) => {
  const { data, oneTemplate, applicationId, resend, closeModal } = props;
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const [hasPressedSend, setHasPressedSend] = useState(false);
  const [smsChecked, setSmsChecked] = useState(
    (data.applicationData?.preSaved && !data.applicationData?.phone) ||
      (resend && !data.applicationForm?.sentToPhone)
      ? false
      : data.sendSms
  );

  const defaultTemplate = oneTemplate
    ? data.templates[0].id.toString()
    : data.applicationData?.template ?? '';

  const [triggerData, setTriggerData] = useState<Omit<TriggerlogData, 'save'>>({
    retrigger: resend ? true : false,
    email: data.applicationData?.email ?? '',
    phone: data.sendSms ? data.applicationData?.phone : '',
    template: defaultTemplate,
    applicationId: applicationId,
  });

  const client = useQueryClient();

  const preSaved = data.applicationData?.preSaved;

  const templateChosen = Boolean(triggerData.template) || data.templates.length === 0;

  const submit = useMutation(
    ({ save }: { save: boolean }) => sendOnboardingForm(applicationId, { ...triggerData, save }),
    {
      mutationKey: ['send-onboarding-form'],
      onSuccess: (data, variables) => {
        if (resend) {
          closeModal?.();
        }
        variables.save
          ? showSuccess(t('integration.submit.save'))
          : showSuccess(t('integrations.onboardingform.form.sent.toast'));
        client.invalidateQueries(['onboarding-form-data', applicationId]);
      },
      onError: (error: SubmitError) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    }
  );
  const noPhoneNumber = (!Boolean(triggerData.phone) || triggerData.phone === '+354') && smsChecked;
  const isInvalidEmail = !validateEmail(triggerData.email);

  const handleSend = () => {
    setHasPressedSend(true);
    if (isInvalidEmail || noPhoneNumber || !templateChosen) {
      return;
    }
    submit.mutate({ save: false });
  };

  const hasChanged = preSaved
    ? triggerData.email !== data.applicationData?.email ||
      triggerData.phone !== data.applicationData?.phone ||
      triggerData.template !== data.applicationData?.template
    : true;

  return (
    <Card variant="transparent" space={4}>
      {submit.isLoading && <Spinner />}
      <Stack space={4}>
        {data.templates.length > 0 && (
          <Select
            options={data.templates.map((template: Template) => {
              return {
                value: template.id.toString(),
                label: template.title,
              };
            })}
            noOptions={() => t('integrations.onboardingform.template.none')}
            onChange={(value) => {
              setTriggerData({ ...triggerData, template: value });
            }}
            label={t('integrations.onboardingform.form.template.label')}
            defaultValue={defaultTemplate}
            error={
              !templateChosen &&
              hasPressedSend &&
              (preSaved || resend) &&
              t('integrations.onboardingform.form.error.template.missing')
            }
          />
        )}
        {(templateChosen || resend || preSaved) && (
          <>
            <Stack space={4}>
              <TextField
                label={t('integrations.onboardingform.form.email')}
                defaultValue={triggerData.email}
                labelIcon={<IconTextFormatLink />}
                onChange={(value) => {
                  setTriggerData({ ...triggerData, email: value });
                }}
                error={
                  hasPressedSend &&
                  isInvalidEmail &&
                  t('integrations.onboardingform.form.error.email.invalid')
                }
              />
              {data.sendSms && (
                <>
                  {smsChecked && (
                    <TextField
                      label="SMS"
                      defaultValue={triggerData.phone}
                      labelIcon={<IconTextFormatLink />}
                      onChange={(value) => {
                        setTriggerData({ ...triggerData, phone: value });
                      }}
                      error={
                        hasPressedSend &&
                        noPhoneNumber &&
                        t('integrations.onboardingform.form.error.phone.missing')
                      }
                    />
                  )}
                  <StyledCheckbox>
                    <Checkbox
                      label={t('integrations.onboardingform.form.sendsms')}
                      defaultValue={smsChecked}
                      onChange={() => {
                        setSmsChecked(!smsChecked);
                        triggerData.phone
                          ? setTriggerData({ ...triggerData, phone: '' })
                          : setTriggerData({ ...triggerData, phone: data.applicationData?.phone });
                      }}
                    />
                  </StyledCheckbox>
                </>
              )}
            </Stack>
            <Stack space={2} orientation="horizontal" justify="flex-end">
              {resend && (
                <Button
                  text={t('application.onboarding.integration.cancel')}
                  variant="secondary"
                  isDisabled={submit.isLoading}
                  onClick={() => {
                    closeModal?.();
                  }}
                />
              )}
              {!resend && (
                <Button
                  text={t('application.onboarding.integration.save')}
                  variant="primary-blue"
                  isDisabled={submit.isLoading || !hasChanged || !templateChosen}
                  onClick={() => {
                    submit.mutate({ save: true });
                  }}
                />
              )}
              <Button
                onClick={() => {
                  handleSend();
                }}
                isDisabled={submit.isLoading}
                iconLeft={<IconSend />}
                text={
                  resend
                    ? t('integrations.onboardingform.form.resend')
                    : t('integrations.dokobit.onboarding.form.submit')
                }
              />
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};

const InProgressForm = (props: {
  data: FormResponse;
  oneTemplate: boolean;
  applicationId: string;
}) => {
  const { data, applicationId, oneTemplate } = props;
  const { lang, t } = useStore();
  const { onCopy } = useClipboard();
  const { showError, showSuccess } = useToaster();
  const { showModal } = useModal();

  useEffect(() => {
    if (data.applicationForm === undefined) {
      showError(t('integrations.onboardingform.form.error.toast'));
    }
  }, [data.applicationForm, showError, showSuccess, t]);

  if (data.applicationForm === undefined) {
    return null;
  }

  const templateId = data.applicationForm.templateId;

  const resendForm = () => {
    showModal('INTEGRATION_ONBOARDING_FORM_RESEND', {
      _container: {
        width: '600px',
      },
      data,
      oneTemplate,
      applicationId,
    });
  };

  const templateTitle = data.templates.find((template) => template.id === templateId)?.title;

  const copyUrl = () => {
    if (data.applicationForm) {
      onCopy(data.applicationForm.url, t('integrations.onboardingform.form.copied.toast'));
    }
  };

  return (
    <Card space={6}>
      <Stack space={6}>
        <Stack space={2}>
          <Text
            color="purple100"
            font="semibold14"
            text={t('integrations.onboardingform.integrationHeader.title')}
          />
          <Text color="purple100" font="regular16" text={templateTitle} />
        </Stack>
        <Separator />
        <Stack space={1}>
          <Text
            color="purple100"
            font="semibold14"
            text={t('integrations.onboardingform.form.datesent')}
          />
          <Text
            color="purple100"
            font="regular16"
            text={abbreviateDate(data.applicationForm.dateCreated, lang, 'datetime')}
          />
        </Stack>
        <Stack space={1}>
          <Text
            color="purple100"
            font="semibold14"
            text={t('integrations.onboardingform.form.sentto.mail')}
          />
          <Text color="purple100" font="regular16" text={data.applicationForm.sentToEmail} />
        </Stack>
        {data.applicationForm.sentToPhone && (
          <Stack space={1}>
            <Text
              color="purple100"
              font="semibold14"
              text={t('integrations.onboardingform.form.sentto.sms')}
            />
            <Text color="purple100" font="regular16" text={data.applicationForm.sentToPhone} />
          </Stack>
        )}
        <Stack space={1}>
          <Text
            color="purple100"
            font="semibold14"
            text={t('integrations.onboardingform.form.sentby')}
          />
          <Text color="purple100" font="regular16" text={data.applicationForm.senderName} />
        </Stack>
        <Stack space={1} orientation="horizontal">
          <IconFormIndicatorPending />
          <Text color="orange100" text={t('integrations.onboardingform.form.inprogress')} />
        </Stack>
        <Stack space={2} orientation="horizontal" justify={'flex-end'}>
          <IconButton
            icon={<IconTextFormatLink />}
            tooltip={t('integrations.onboardingform.form.tooltip.copylink')}
            onClick={() => {
              copyUrl();
            }}
          />
          <Button
            iconLeft={<IconRedo />}
            text={t('integrations.onboardingform.form.resend')}
            variant="secondary"
            tooltip={t('integrations.onboardingform.form.tooltip.resend')}
            onClick={() => resendForm()}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

const CompletedForm = (props: { data: FormResponse }) => {
  const { data } = props;
  const { t, lang } = useStore();
  const { showError, showSuccess } = useToaster();

  useEffect(() => {
    if (data.applicationForm === undefined) {
      showError(t('integrations.onboardingform.form.error.toast'));
    }
  }, [data.applicationForm, showError, showSuccess, t]);

  if (data.applicationForm === undefined) {
    return null;
  }

  const templateId = data.applicationForm.templateId;

  const templateTitle = data.templates.find((template) => template.id === templateId)?.title;

  return (
    <Card space={6}>
      <Stack space={6}>
        <Stack space={2}>
          <Text
            color="purple100"
            font="semibold14"
            text={t('integrations.onboardingform.integrationHeader.title')}
          />
          <Text text={templateTitle} />
        </Stack>
        <RenderForm form={data.applicationForm.form} />
        <Separator />
        <Stack space={4}>
          <Stack space={1}>
            <Text
              color="purple100"
              font="semibold14"
              text={t('integrations.onboardingform.form.datesent')}
            />
            <Text
              color="purple100"
              font="regular16"
              text={abbreviateDate(data.applicationForm.dateCreated, lang, 'datetime')}
            />
          </Stack>
          <Stack space={1}>
            <Text
              color="purple100"
              font="semibold14"
              text={t('integrations.onboardingform.form.sentto.mail')}
            />
            <Text color="purple100" font="regular16" text={data.applicationForm.sentToEmail} />
          </Stack>
          {data.applicationForm.sentToPhone && (
            <Stack space={1}>
              <Text
                color="purple100"
                font="semibold14"
                text={t('integrations.onboardingform.form.sentto.sms')}
              />
              <Text color="purple100" font="regular16" text={data.applicationForm.sentToPhone} />
            </Stack>
          )}
          <Stack space={1}>
            <Text
              color="purple100"
              font="semibold14"
              text={t('integrations.onboardingform.form.sentby')}
            />
            <Text color="purple100" font="regular16" text={data.applicationForm.senderName} />
          </Stack>
          <Stack space={1} orientation="horizontal">
            <IconFormIndicatorSuccess />
            <Text color="green100" text={t('integrations.onboardingform.form.completed')} />
          </Stack>
          <Stack space={1}>
            <Text
              color="purple100"
              font="semibold14"
              text={t('integrations.onboardingform.form.dateCompleted')}
            />
            <Text
              color="purple100"
              font="regular16"
              text={abbreviateDate(data.applicationForm.dateCompleted, lang, 'datetime')}
            />
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

const RenderForm = (props: { form: RenderFormProps[] }) => {
  const { form } = props;
  const {
    t,
    lang,
  }: { t: (arg0: string, ...args: string[]) => string; lang: AdminSiteLanguageType } = useStore();
  const fallbackLang = lang !== 'en' ? 'en' : 'is';

  return (
    <>
      {form.map((section, index) => (
        <Stack space={4} key={index}>
          <Separator />
          {section.h3 && (
            <Text
              color="purple100"
              font="bold18"
              text={
                typeof section.h3 === 'string'
                  ? section.h3
                  : section.h3[lang] ?? section.h3[fallbackLang]
              }
            />
          )}
          {section.answers.map((answer, index) => (
            <Stack space={2} key={index}>
              <Text
                color="purple100"
                font="semibold14"
                text={
                  typeof answer.label === 'string'
                    ? answer.label
                    : answer.label[lang] ?? answer.label[fallbackLang]
                }
              />
              {!answer.value ? (
                <Text
                  color="purple50"
                  font="regular16"
                  text={t('integrations.onboardingform.form.answer.missing')}
                />
              ) : answer.type === 'file' ? (
                <FilePreviewCard value={answer.value} />
              ) : answer.type === 'file-multi' ? (
                <Stack space={1}>
                  {answer.value.map((val, index) => (
                    <FilePreviewCard key={index} value={val} />
                  ))}
                </Stack>
              ) : answer.type === 'dateField' ? (
                <Text
                  color="purple100"
                  font="regular16"
                  text={abbreviateDate(answer.value, lang, 'date')}
                />
              ) : (
                <Text color="purple100" font="regular16" text={answer.value} />
              )}
            </Stack>
          ))}
        </Stack>
      ))}
    </>
  );
};

const FilePreviewCard = ({ value }: { value: FileProps }) => {
  const { showFullscreenModal } = useModal();

  return (
    <UploadedFile
      name={value.fileName}
      path={value.valueOriginal}
      onPreview={() => {
        showFullscreenModal('INTEGRATION_FILE_PREVIEW', {
          data: value,
        });
      }}
    />
  );
};

const StyledCheckbox = styled.div({
  cursor: 'pointer',
});
